import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import DataReactTable from "../DataTable/components/DataReactTable";
import { useSelector } from "react-redux";
import VehicleComponent from "../../shared/components/table/components/VehicleComponent";
import {
  AddFormButton,
  ColumnHeading,
} from "../../shared/components/TableElements";
import { Link } from "react-router-dom";
import {
  formatNumber,
  getDistanceUnit,
  getListingDisplayDate,
  getTranslatedTransactionType,
} from "../../shared/helpers";
import { NATEXT } from "../../shared/constants";
import getUnixTime from "date-fns/getUnixTime";
import ExplorePopup from "../../shared/components/ExplorePopup";
import { LinkSpan } from "../../shared/components/form/FormElements";
import useFetchData from "../../shared/custom-hooks/useFetchData";
import { dhm } from "../WorkOrders";
import getDefaultFilterData from "../../shared/constants/getDefaultFilterData";
import { fetchServiceTaskTemplates } from "../../features/servicesTasks/servicesTasksSlice";
import { useDispatch } from "react-redux";
import { useAuth } from "../../contexts/AuthContext";

const ServicesTable = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const { isViewOnly } = useAuth();
  const columnWidth = 130;

  const rowData = useSelector((state) => state.filter.rowData);
  const servicesValues = useSelector((state) => state.filter.values);

  const { distance, currency } = useSelector((state) => state.units);

  const { minDate, maxDate, noOfRecords } = useSelector(
    (state) => state.columns,
  );

  const [showExplorePopup, setShowExplorePopup] = useState(false);

  const { defaultContainsData, defaultRangeData, defaultTransactionTypeData } =
    getDefaultFilterData();

  const { templatesStatus } = useSelector((state) => state.servicesTasks);

  //Effect to fetch templates
  useEffect(() => {
    if (templatesStatus === "idle") {
      dispatch(
        fetchServiceTaskTemplates({
          querystring: "fetch_all_service_task_templates",
        }),
      );
    }
  }, [templatesStatus, dispatch]);

  const initialData = useMemo(
    () => ({
      date: {
        column: "date",
        name: t("date_tv"),
        dateOption: "all_time",
        from: new Date(minDate * 1000),
        to: new Date(),
        isFiltered: false,
      },
      odo: {
        column: "odo",
        name: t("odo"),
        ...defaultRangeData,
      },
      serviceStation: {
        column: "serviceStation",
        name: t("service_station"),
        ...defaultContainsData,
      },
      serviceTask: {
        column: "serviceTask",
        name: t("service_task"),
        ...defaultContainsData,
      },
      partNames: {
        column: "partNames",
        name: t("parts_consumed"),
        ...defaultContainsData,
      },
      totalCost: {
        column: "totalCost",
        name: t("tc_tv"),
        ...defaultRangeData,
      },
      totalLaborCost: {
        column: "totalLaborCost",
        name: t("labor_cost"),
        ...defaultRangeData,
      },
      totalPartsCost: {
        column: "totalPartsCost",
        name: t("parts_cost"),
        ...defaultRangeData,
      },
      transactionType: {
        column: "transactionType",
        name: t("transaction_type"),
        ...defaultTransactionTypeData,
      },
      cardNumber: {
        column: "cardNumber",
        name: t("card_number"),
        ...defaultContainsData,
      },
      servicedBy: {
        column: "servicedBy",
        name: t("serviced_by"),
        ...defaultContainsData,
      },
      comments: {
        column: "comments",
        name: t("notes_tv"),
        ...defaultContainsData,
      },
    }),
    [minDate],
  );

  useFetchData({
    startDate: 0,
    endDate: getUnixTime(new Date()),
    query: "get_all_services",
    sortIndex: 2,
  });

  const servicesValuesArray = [
    {
      id: 0,
      name: t("cost"),
      value: servicesValues.footer_total_cost
        ? `${formatNumber(servicesValues.footer_total_cost, 2)} ${currency}`
        : NATEXT,
    },
    {
      id: 1,
      name: t("labor_cost"),
      value: servicesValues.footer_total_labor_cost
        ? `${formatNumber(
            servicesValues.footer_total_labor_cost,
            2,
          )} ${currency}`
        : NATEXT,
    },
    {
      id: 2,
      name: t("parts_cost"),
      value: servicesValues.footer_total_parts_cost
        ? `${formatNumber(
            servicesValues.footer_total_parts_cost,
            2,
          )} ${currency}`
        : NATEXT,
    },
    {
      id: 3,
      name: t("total_duration"),
      value: servicesValues.footer_total_time
        ? dhm(servicesValues.footer_total_time * 1000)
        : NATEXT,
    },
  ];

  const columnHeaders = useMemo(
    () => [
      {
        id: 1,
        Header: t("asset"),
        accessor: "name",
        // Footer: 'Middle age:',
        // disableGlobalFilter: false,
        width: 130,
        disableFilters: true,
        Cell: ({ row }) => (
          <VehicleComponent
            vehicleName={row.original.name}
            //groupName={row.original.group_name}
            vehicleId={row.original.veh_id}
            vehicleImg={row.original.veh_img}
            orgId={row.original.org_id}
            assetType={row.original.asset_type}
          />
        ),
      },
      {
        id: 2,
        Header: t("date_tv"),
        accessor: "service_date",
        //disableFilters: true,
        width: columnWidth,
        disableCol: true,
        //minWidth: 50,
        //Cell: ({ value }) => (value ? getDisplayDate(value) : NATEXT),
        Cell: ({ value }) => (value ? getListingDisplayDate(value) : NATEXT),
      },
      {
        id: 3,
        Header: t("odo"),
        accessor: "odo",
        //disableFilters: true,
        width: columnWidth,
        Cell: ({ row }) => {
          const { odo, primary_meter } = row?.original;
          const odoUnit =
            primary_meter === "Hours"
              ? getDistanceUnit(primary_meter)
              : getDistanceUnit(distance);
          return odo && odo >= 0 ? `${formatNumber(odo)} ${odoUnit}` : NATEXT;
        },
      },
      {
        id: 4,
        Header: t("service_station"),
        accessor: "service_station",
        //disableFilters: true,
        width: columnWidth,
        Cell: ({ value }) => (value ? value : NATEXT),
        // disableGlobalFilter: false,
      },
      {
        id: 5,
        Header: t("service_task"),
        accessor: "service_task_name",
        //disableFilters: true,
        width: columnWidth,
        accessorId: "task_name",
        Cell: ({ value }) => (value ? value : NATEXT),
        // disableGlobalFilter: false,
      },
      {
        id: 6,
        Header: t("tc_tv"),
        accessor: "total",
        // Cell: ({ row }) => {
        //   const { discount, tax, total } = row?.original;
        //   if (+total && +total > 0) {
        //     const grandTotal = +total - +discount + +tax;
        //     return `${formatNumber(grandTotal, 2)} ${currency}`;
        //   } else {
        //     return NATEXT;
        //   }
        // },
        Cell: ({ value }) =>
          value ? `${formatNumber(value, 2)} ${currency}` : NATEXT,
      },
      {
        id: 7,
        Header: t("labor_cost"),
        accessor: "labor_total",
        //disableFilters: true,
        width: columnWidth,
        Cell: ({ value }) =>
          value ? `${formatNumber(value, 2)} ${currency}` : NATEXT,
      },
      {
        id: 8,
        Header: t("parts_cost"),
        accessor: "parts_total",
        //disableFilters: true,
        width: columnWidth,
        Cell: ({ value }) =>
          value ? `${formatNumber(value, 2)} ${currency}` : NATEXT,
      },
      {
        id: 9,
        Header: t("transaction_type"),
        accessor: "transaction_type",
        disableSortBy: true,
        Cell: ({ value }) =>
          value ? getTranslatedTransactionType(value, t) : NATEXT,
        width: columnWidth,
      },
      {
        id: 10,
        Header: t("card_number"),
        accessor: "card_number",
        //disableFilters: true,
        Cell: ({ value }) => (+value ? +value : NATEXT),
        width: columnWidth,
      },
      {
        id: 11,
        Header: t("serviced_by"),
        accessor: "uname",
        //disableFilters: true,
        //Cell: ({ value }) => users?.find((u) => u.user_id === value)?.label,
        width: columnWidth,
      },
      {
        id: 12,
        Header: t("notes_tv"),
        accessor: "comments",
        //disableFilters: true,
        width: columnWidth,
        accessorId: "comments",
        Cell: ({ value }) => (value ? value : NATEXT),
      },
      {
        id: 13,
        Header: t("total_duration"),
        accessor: "total_time",
        width: columnWidth,
        Cell: ({ value }) => (value ? dhm(value * 1000) : NATEXT),
      },
      {
        id: 14,
        Header: t("parts_consumed"),
        accessor: "part_names",
        accessorId: "task_name",
        Cell: ({ value }) => (value ? value : NATEXT),
      },
      // {
      //   id: 12,
      //   Header: "Group Name",
      //   accessor: "group_name",
      //   //disableFilters: true,
      //   width: columnWidth,
      // },
      // {
      //   id: 'edit',
      //   Header: "",
      //   accessor: "edit",
      //   accessorId: "edit",
      //   Cell: () => <RowOptions />,
      //   disableSortBy: true,
      // },
    ],
    [rowData, currency, distance],
  );

  const reactTableData = {
    tableHeaderData: columnHeaders,
    tableRowsData: rowData,
  };

  return (
    <>
      {showExplorePopup && (
        <ExplorePopup
          showPopup={showExplorePopup}
          setShowPopup={setShowExplorePopup}
          exploreLink={"https://embed.getsmartcue.com/IAFSOVME"}
        />
      )}
      <Container>
        <Row className="mx-1 d-flex align-items-center justify-content-around">
          {/* <Col sm={10}>
          <ColumnHeading>{t("services")}</ColumnHeading>
        </Col> */}
          <Col className="d-flex align-items-center gap-2">
            <ColumnHeading>{t("services")}</ColumnHeading>
            {/* <FilterButton onClick={() => setShowExplorePopup(true)}>
              <i
                className="fi fi-rr-globe"
                style={{ margin: "4px 4px 0 0" }}
              ></i>
              <span>{t("explore")}</span>
            </FilterButton> */}
            <LinkSpan
              onClick={() => setShowExplorePopup(true)}
              style={{ alignSelf: "end" }}
            >
              {t("explore")}
            </LinkSpan>
          </Col>
          <Col sm={2} className="d-flex justify-content-end align-items-center">
            {!isViewOnly && (
              <Link to={"/services/services/addnew"}>
                <AddFormButton id="add-service-btn">
                  {t("add_service")}
                </AddFormButton>
              </Link>
            )}
          </Col>
        </Row>
        <Row>
          <DataReactTable
            reactTableData={reactTableData}
            valuesArray={servicesValuesArray}
            initialData={initialData}
          />
        </Row>
      </Container>
    </>
  );
  //return <div>Hello World</div>;
};

export default ServicesTable;
